if (!customElements.get("td-modal")) {
  class tdModal extends HTMLElement {
    constructor() {
      super();
      this.modalTrigger = this.querySelector('[data-icon-modal-trigger]');
      this.modalContent = this.querySelector('.td-ugc__modal');
      this.modalOverlayContent = this.querySelector('.td-ugc__overlay');
      this.closeButton = this.querySelector('.td-ugc__close-button');
      this.viewButton = this.querySelector('[data-view-product-btn]');
      this.mainContainer = document.querySelector('#main');
      this.body = document.querySelector('body');
      this.dragThreshold = 10;
    }

    connectedCallback() {
      if (!this.modalTrigger || !this.modalContent || !this.modalOverlayContent || !this.closeButton || !this.viewButton) {
        console.error('Missing required elements');
        return;
      }
      this.modalTrigger.addEventListener('mousedown', this.handleMouseDown.bind(this));
      this.modalTrigger.addEventListener('mouseup', this.handleMouseUp.bind(this));
      this.modalTrigger.addEventListener('click', this.handleClick.bind(this));
      this.modalTrigger.addEventListener('keydown', this.handleKeydown.bind(this));
      this.modalOverlayContent.addEventListener('click', this.closeModal.bind(this));
      this.closeButton.addEventListener('click', this.closeModal.bind(this));
      this.viewButton.addEventListener('click', this.closeModal.bind(this));
    }

    handleMouseDown(event) {
      this.startX = event.clientX;
      this.startY = event.clientY;
      this.isDragging = false;
    }

    handleMouseUp(event) {
      const endX = event.clientX;
      const endY = event.clientY;

      if (Math.abs(endX - this.startX) > this.dragThreshold || Math.abs(endY - this.startY) > this.dragThreshold) {
        this.isDragging = true;
      }
    }

    handleClick() {
      if (!this.isDragging) {
        this.openModal();
      }
    }

    handleKeydown(event) {
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        this.openModal();
      }
    }

    getFocusableElements() {
      const focusableSelectors = [
        'a[href]',
        'button:not([disabled])',
        'input:not([disabled])',
        'select:not([disabled])',
        'textarea:not([disabled])',
        '[tabindex]:not([tabindex="-1"])'
      ];

      return Array.from(
        this.modalContent.querySelectorAll(focusableSelectors.join(','))
      ).filter(el => !el.hasAttribute('inert'));
    }

    handleModalKeydown(event) {
      // Close modal on Escape
      if (event.key === 'Escape') {
        event.preventDefault();
        this.closeModal();
        return;
      }

      // Trap focus on Tab/Shift+Tab
      if (event.key === 'Tab') {
        const focusableElements = this.getFocusableElements();
        if (focusableElements.length === 0) {
          return;
        }

        const firstElement = focusableElements[0];
        const lastElement = focusableElements[focusableElements.length - 1];

        if (event.shiftKey) {
          // Shift+Tab: if on first element, move to last
          if (document.activeElement === firstElement) {
            event.preventDefault();
            lastElement.focus();
          }
        } else {
          // Tab: if on last element, move to first
          if (document.activeElement === lastElement) {
            event.preventDefault();
            firstElement.focus();
          }
        }
      }
    }

    openModal() {
      // Store the trigger element to return focus later
      this.previouslyFocusedElement = document.activeElement;

      this.modalContent.style.display = 'block';
      this.modalOverlayContent.style.display = 'block';

      requestAnimationFrame(() => {
        this.modalContent.classList.add('td-ugc__modal--active');
        this.modalOverlayContent.classList.add('td-ugc__overlay--active');
      });

      this.body.style.overflow = 'hidden';

      this.mainContainer.prepend(this.modalContent);
      this.mainContainer.prepend(this.modalOverlayContent);

      const video = this.modalContent.querySelector('video');
      if (video) {
        video.play();
        video.muted = false;
      }

      document.dispatchEvent(new CustomEvent('pause-swiper'));

      // Attach focus trap handler
      this.boundHandleModalKeydown = this.handleModalKeydown.bind(this);
      this.modalContent.addEventListener('keydown', this.boundHandleModalKeydown);

      // Move focus to close button after modal opens
      setTimeout(() => {
        this.closeButton.focus();
      }, 100);
    }

    closeModal() {
      this.modalContent.classList.remove('td-ugc__modal--active');
      this.modalOverlayContent.classList.remove('td-ugc__overlay--active');

      // Remove focus trap handler
      if (this.boundHandleModalKeydown) {
        this.modalContent.removeEventListener('keydown', this.boundHandleModalKeydown);
      }

      this.modalContent.addEventListener('transitionend', () => {
        this.modalContent.style.display = 'none';
        this.modalOverlayContent.style.display = 'none';
      }, { once: true });

      this.body.style.overflow = 'visible';

      const video = this.modalContent.querySelector('video');
      if (video) {
        video.pause();
        video.currentTime = 0;
      }

      document.dispatchEvent(new CustomEvent('reset-swiper'));

      // Return focus to the element that opened the modal
      if (this.previouslyFocusedElement) {
        this.previouslyFocusedElement.focus();
      }
    }
  }

  customElements.define("td-modal", tdModal);
}
